/**
 * Layer Manager - Photoshop-like layer system for T-Shirt Design Studio
 * Phase 1: Core layer management, reordering, visibility, locking, properties
 */

(function() {
    'use strict';

    class LayerManager {
        constructor(sectionId, containerId) {
            this.sectionId = sectionId;
            this.containerId = containerId;
            this.layers = [];
            this.selectedLayerId = null;
            this.nextLayerId = 1;
            this.undoStack = [];
            this.redoStack = [];
            this.maxUndo = 50;
            
            this.init();
        }

        init() {
            // Initialize with default text layer if no layers exist
            if (this.layers.length === 0) {
                this.createTextLayer('YOUR DESIGN HERE', {
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 32,
                    color: '#000000',
                    x: 50,
                    y: 50
                });
            }
            
            this.setupKeyboardShortcuts();
            console.log('✅ Layer Manager initialized');
        }

        /**
         * Create a new text layer
         */
        createTextLayer(content, properties = {}) {
            const layer = {
                id: `layer-${this.nextLayerId++}`,
                type: 'text',
                name: properties.name || `Text Layer ${this.layers.length + 1}`,
                content: content || '',
                visible: properties.visible !== false,
                locked: properties.locked === true,
                opacity: properties.opacity !== undefined ? properties.opacity : 100,
                blendMode: properties.blendMode || 'normal',
                zIndex: this.layers.length,
                
                // Text properties
                fontFamily: properties.fontFamily || "'Bebas Neue', sans-serif",
                fontSize: properties.fontSize || 32,
                color: properties.color || '#000000',
                fontWeight: properties.fontWeight || 'normal',
                fontStyle: properties.fontStyle || 'normal',
                textAlign: properties.textAlign || 'center',
                letterSpacing: properties.letterSpacing || 0,
                
                // Transform properties
                x: properties.x !== undefined ? properties.x : 50,
                y: properties.y !== undefined ? properties.y : 50,
                rotation: properties.rotation || 0,
                scaleX: properties.scaleX !== undefined ? properties.scaleX : 1,
                scaleY: properties.scaleY !== undefined ? properties.scaleY : 1,
                flipH: properties.flipH === true,
                flipV: properties.flipV === true,
                
                // Filters (for future use)
                filters: {
                    brightness: 0,
                    contrast: 0,
                    saturation: 0
                },
                
                // DOM element reference (will be set when rendered)
                element: null
            };
            
            this.layers.push(layer);
            this.saveState();
            this.renderLayers();
            this.updateLayersPanel();
            
            return layer.id;
        }

        /**
         * Create a new image layer
         */
        createImageLayer(src, properties = {}) {
            const layer = {
                id: `layer-${this.nextLayerId++}`,
                type: 'image',
                name: properties.name || `Image Layer ${this.layers.filter(l => l.type === 'image').length + 1}`,
                src: src,
                visible: properties.visible !== false,
                locked: properties.locked === true,
                opacity: properties.opacity !== undefined ? properties.opacity : 100,
                blendMode: properties.blendMode || 'normal',
                zIndex: this.layers.length,
                
                // Transform properties
                x: properties.x !== undefined ? properties.x : 50,
                y: properties.y !== undefined ? properties.y : 50,
                width: properties.width || 200,
                height: properties.height || 200,
                rotation: properties.rotation || 0,
                scaleX: properties.scaleX !== undefined ? properties.scaleX : 1,
                scaleY: properties.scaleY !== undefined ? properties.scaleY : 1,
                flipH: properties.flipH === true,
                flipV: properties.flipV === true,
                
                // Filters
                filters: {
                    brightness: properties.filters?.brightness || 0,
                    contrast: properties.filters?.contrast || 0,
                    saturation: properties.filters?.saturation || 0
                },
                
                // DOM element reference
                element: null
            };
            
            this.layers.push(layer);
            this.saveState();
            this.renderLayers();
            this.updateLayersPanel();
            
            return layer.id;
        }

        /**
         * Get layer by ID
         */
        getLayer(layerId) {
            return this.layers.find(l => l.id === layerId);
        }

        /**
         * Get selected layer
         */
        getSelectedLayer() {
            return this.selectedLayerId ? this.getLayer(this.selectedLayerId) : null;
        }

        /**
         * Select a layer
         */
        selectLayer(layerId) {
            this.selectedLayerId = layerId;
            this.updateLayersPanel();
            this.updatePropertiesPanel();
        }

        /**
         * Delete a layer
         */
        deleteLayer(layerId) {
            const index = this.layers.findIndex(l => l.id === layerId);
            if (index === -1) return false;
            
            const layer = this.layers[index];
            if (layer.element) {
                layer.element.remove();
            }
            
            this.layers.splice(index, 1);
            
            // Update z-index for remaining layers
            this.layers.forEach((l, i) => {
                l.zIndex = i;
            });
            
            if (this.selectedLayerId === layerId) {
                this.selectedLayerId = this.layers.length > 0 ? this.layers[this.layers.length - 1].id : null;
            }
            
            this.saveState();
            this.renderLayers();
            this.updateLayersPanel();
            this.updatePropertiesPanel();
            
            return true;
        }

        /**
         * Toggle layer visibility
         */
        toggleLayerVisibility(layerId) {
            const layer = this.getLayer(layerId);
            if (!layer) return;
            
            layer.visible = !layer.visible;
            this.saveState();
            this.renderLayers();
            this.updateLayersPanel();
        }

        /**
         * Toggle layer lock
         */
        toggleLayerLock(layerId) {
            const layer = this.getLayer(layerId);
            if (!layer) return;
            
            layer.locked = !layer.locked;
            this.saveState();
            this.updateLayersPanel();
        }

        /**
         * Rename a layer
         */
        renameLayer(layerId, newName) {
            const layer = this.getLayer(layerId);
            if (!layer) return false;
            
            layer.name = newName.trim() || layer.name;
            this.saveState();
            this.updateLayersPanel();
            
            return true;
        }

        /**
         * Reorder layers (move layer to new index)
         */
        reorderLayer(layerId, newIndex) {
            const currentIndex = this.layers.findIndex(l => l.id === layerId);
            if (currentIndex === -1) return false;
            
            const layer = this.layers.splice(currentIndex, 1)[0];
            this.layers.splice(newIndex, 0, layer);
            
            // Update z-index
            this.layers.forEach((l, i) => {
                l.zIndex = i;
            });
            
            this.saveState();
            this.renderLayers();
            this.updateLayersPanel();
            
            return true;
        }

        /**
         * Bring layer forward
         */
        bringForward(layerId) {
            const index = this.layers.findIndex(l => l.id === layerId);
            if (index === -1 || index === this.layers.length - 1) return false;
            
            return this.reorderLayer(layerId, index + 1);
        }

        /**
         * Send layer backward
         */
        sendBackward(layerId) {
            const index = this.layers.findIndex(l => l.id === layerId);
            if (index === -1 || index === 0) return false;
            
            return this.reorderLayer(layerId, index - 1);
        }

        /**
         * Bring layer to front
         */
        bringToFront(layerId) {
            return this.reorderLayer(layerId, this.layers.length - 1);
        }

        /**
         * Send layer to back
         */
        sendToBack(layerId) {
            return this.reorderLayer(layerId, 0);
        }

        /**
         * Center layer horizontally in print area
         */
        centerHorizontally(layerId) {
            const layer = this.getLayer(layerId);
            if (!layer) return false;
            
            // Print area is typically centered, assume 50% for horizontal center
            layer.x = 50;
            this.saveState();
            this.renderLayers();
            this.updatePropertiesPanel();
            
            return true;
        }

        /**
         * Center layer vertically in print area
         */
        centerVertically(layerId) {
            const layer = this.getLayer(layerId);
            if (!layer) return false;
            
            // Print area is typically centered, assume 50% for vertical center
            layer.y = 50;
            this.saveState();
            this.renderLayers();
            this.updatePropertiesPanel();
            
            return true;
        }

        /**
         * Update layer property
         */
        updateLayerProperty(layerId, property, value) {
            const layer = this.getLayer(layerId);
            if (!layer) return false;
            
            if (property.includes('.')) {
                // Nested property (e.g., 'filters.brightness')
                const parts = property.split('.');
                let obj = layer;
                for (let i = 0; i < parts.length - 1; i++) {
                    if (!obj[parts[i]]) obj[parts[i]] = {};
                    obj = obj[parts[i]];
                }
                obj[parts[parts.length - 1]] = value;
            } else {
                layer[property] = value;
            }
            
            this.saveState();
            this.renderLayers();
            this.updatePropertiesPanel();
            
            return true;
        }

        /**
         * Render all layers to the canvas
         */
        renderLayers() {
            const container = document.getElementById(this.containerId);
            if (!container) {
                console.warn(`Container ${this.containerId} not found`);
                return;
            }
            
            // Ensure container has proper positioning
            const containerStyle = window.getComputedStyle(container);
            if (containerStyle.position === 'static') {
                container.style.position = 'relative';
            }
            
            // Clear existing layer elements
            this.layers.forEach(layer => {
                if (layer.element) {
                    layer.element.remove();
                }
            });
            
            // Render layers in order (back to front)
            this.layers.forEach(layer => {
                if (!layer.visible) return;
                
                let element;
                
                if (layer.type === 'text') {
                    element = document.createElement('span');
                    element.className = `cts-layer-text-${this.sectionId}`;
                    element.id = layer.id;
                    element.textContent = layer.content;
                    element.style.position = 'absolute';
                    element.style.left = layer.x + '%';
                    element.style.top = layer.y + '%';
                    element.style.transform = `translate(-50%, -50%) rotate(${layer.rotation}deg) scaleX(${layer.scaleX * (layer.flipH ? -1 : 1)}) scaleY(${layer.scaleY * (layer.flipV ? -1 : 1)})`;
                    element.style.fontFamily = layer.fontFamily;
                    element.style.fontSize = layer.fontSize + 'px';
                    element.style.color = layer.color;
                    element.style.fontWeight = layer.fontWeight;
                    element.style.fontStyle = layer.fontStyle;
                    element.style.textAlign = layer.textAlign;
                    element.style.letterSpacing = layer.letterSpacing + 'px';
                    element.style.opacity = layer.opacity / 100;
                    element.style.zIndex = layer.zIndex;
                    element.style.pointerEvents = layer.locked ? 'none' : 'auto';
                    element.style.cursor = layer.locked ? 'not-allowed' : 'move';
                    element.style.userSelect = 'none';
                    
                    // Apply blend mode
                    if (layer.blendMode !== 'normal') {
                        element.style.mixBlendMode = layer.blendMode;
                    }
                    
                    // Apply filters
                    const filterStr = this.buildFilterString(layer.filters);
                    if (filterStr) {
                        element.style.filter = filterStr;
                    }
                    
                } else if (layer.type === 'image') {
                    element = document.createElement('img');
                    element.className = `cts-layer-image-${this.sectionId}`;
                    element.id = layer.id;
                    element.src = layer.src;
                    element.style.position = 'absolute';
                    element.style.left = layer.x + '%';
                    element.style.top = layer.y + '%';
                    element.style.transform = `translate(-50%, -50%) rotate(${layer.rotation}deg) scaleX(${layer.scaleX * (layer.flipH ? -1 : 1)}) scaleY(${layer.scaleY * (layer.flipV ? -1 : 1)})`;
                    element.style.width = layer.width + 'px';
                    element.style.height = layer.height + 'px';
                    element.style.opacity = layer.opacity / 100;
                    element.style.zIndex = layer.zIndex;
                    element.style.pointerEvents = layer.locked ? 'none' : 'auto';
                    element.style.cursor = layer.locked ? 'not-allowed' : 'move';
                    element.style.userSelect = 'none';
                    element.style.objectFit = 'contain';
                    
                    // Apply blend mode
                    if (layer.blendMode !== 'normal') {
                        element.style.mixBlendMode = layer.blendMode;
                    }
                    
                    // Apply filters
                    const filterStr = this.buildFilterString(layer.filters);
                    if (filterStr) {
                        element.style.filter = filterStr;
                    }
                } else if (layer.type === 'brush') {
                    // Brush layer uses canvas element
                    element = layer.canvas || layer.element;
                    if (!element) {
                        // Recreate canvas if missing
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        canvas.width = layer.width || 800;
                        canvas.height = layer.height || 600;
                        canvas.style.position = 'absolute';
                        canvas.style.top = '0';
                        canvas.style.left = '0';
                        canvas.style.width = '100%';
                        canvas.style.height = '100%';
                        canvas.style.pointerEvents = layer.locked ? 'none' : 'auto';
                        canvas.style.userSelect = 'none';
                        canvas.className = `cts-brush-layer-${this.sectionId}`;
                        canvas.id = layer.id;
                        
                        layer.canvas = canvas;
                        layer.ctx = ctx;
                        element = canvas;
                    }
                    
                    element.style.opacity = layer.opacity / 100;
                    element.style.zIndex = layer.zIndex;
                    element.style.pointerEvents = layer.locked ? 'none' : 'auto';
                    
                    // Apply blend mode
                    if (layer.blendMode !== 'normal') {
                        element.style.mixBlendMode = layer.blendMode;
                    }
                    
                    // Redraw paths if they exist
                    if (layer.paths && layer.paths.length > 0 && window.brushTool) {
                        layer.ctx.clearRect(0, 0, layer.width, layer.height);
                        layer.paths.forEach(path => {
                            window.brushTool.redrawPath(layer, path);
                        });
                    }
                }
                
                if (element) {
                    // Add selection highlight
                    if (layer.id === this.selectedLayerId) {
                        element.style.outline = '2px solid rgba(96, 165, 250, 0.8)';
                        element.style.outlineOffset = '2px';
                    }
                    
                    container.appendChild(element);
                    layer.element = element;
                }
            });
        }

        /**
         * Build CSS filter string from filters object
         */
        buildFilterString(filters) {
            const parts = [];
            if (filters.brightness !== 0) {
                parts.push(`brightness(${100 + filters.brightness}%)`);
            }
            if (filters.contrast !== 0) {
                parts.push(`contrast(${100 + filters.contrast}%)`);
            }
            if (filters.saturation !== 0) {
                parts.push(`saturate(${100 + filters.saturation}%)`);
            }
            return parts.length > 0 ? parts.join(' ') : '';
        }

        /**
         * Update layers panel UI
         */
        updateLayersPanel() {
            const panel = document.getElementById(`layersPanel-${this.sectionId}`);
            if (!panel) return;
            
            const list = panel.querySelector(`.cts-layers-list-${this.sectionId}`);
            if (!list) return;
            
            // Clear and rebuild
            list.innerHTML = '';
            
            // Render layers in reverse order (top to bottom = front to back)
            [...this.layers].reverse().forEach(layer => {
                const item = document.createElement('div');
                item.className = `cts-layer-item-${this.sectionId}`;
                item.dataset.layerId = layer.id;
                if (layer.id === this.selectedLayerId) {
                    item.classList.add('active');
                }
                
                item.innerHTML = `
                    <div class="cts-layer-item-header-${this.sectionId}">
                        <button class="cts-layer-visibility-${this.sectionId}" 
                                data-layer-id="${layer.id}"
                                title="${layer.visible ? 'Hide' : 'Show'}">
                            ${layer.visible ? '👁️' : '👁️‍🗨️'}
                        </button>
                        <button class="cts-layer-lock-${this.sectionId}" 
                                data-layer-id="${layer.id}"
                                title="${layer.locked ? 'Unlock' : 'Lock'}">
                            ${layer.locked ? '🔒' : '🔓'}
                        </button>
                        <span class="cts-layer-name-${this.sectionId}" 
                              data-layer-id="${layer.id}">${this.escapeHtml(layer.name)}</span>
                        <span class="cts-layer-type-${this.sectionId}">${layer.type === 'text' ? 'T' : layer.type === 'image' ? 'I' : layer.type === 'brush' ? 'B' : '?'}</span>
                    </div>
                `;
                
                // Make draggable
                item.draggable = true;
                item.addEventListener('dragstart', (e) => {
                    e.dataTransfer.setData('text/plain', layer.id);
                    item.classList.add('dragging');
                });
                
                item.addEventListener('dragend', () => {
                    item.classList.remove('dragging');
                });
                
                // Click to select
                item.addEventListener('click', (e) => {
                    if (!e.target.closest('button')) {
                        this.selectLayer(layer.id);
                    }
                });
                
                // Double-click to rename
                item.addEventListener('dblclick', () => {
                    this.startRenaming(layer.id);
                });
                
                list.appendChild(item);
            });
            
            // Setup event listeners
            this.setupLayerPanelListeners();
        }

        /**
         * Start renaming a layer
         */
        startRenaming(layerId) {
            const layer = this.getLayer(layerId);
            if (!layer) return;
            
            const item = document.querySelector(`[data-layer-id="${layerId}"]`);
            if (!item) return;
            
            const nameSpan = item.querySelector(`.cts-layer-name-${this.sectionId}`);
            if (!nameSpan) return;
            
            const input = document.createElement('input');
            input.type = 'text';
            input.value = layer.name;
            input.className = `cts-layer-name-input-${this.sectionId}`;
            input.style.width = '100%';
            input.style.padding = '2px 4px';
            input.style.border = '1px solid rgba(96, 165, 250, 0.6)';
            input.style.borderRadius = '4px';
            input.style.background = 'rgba(0, 0, 0, 0.8)';
            input.style.color = 'white';
            input.style.fontSize = '12px';
            
            nameSpan.replaceWith(input);
            input.focus();
            input.select();
            
            const finishRenaming = () => {
                const newName = input.value.trim();
                this.renameLayer(layerId, newName || layer.name);
            };
            
            input.addEventListener('blur', finishRenaming);
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    finishRenaming();
                } else if (e.key === 'Escape') {
                    input.blur();
                }
            });
        }

        /**
         * Setup event listeners for layers panel
         */
        setupLayerPanelListeners() {
            // Visibility toggle
            document.querySelectorAll(`.cts-layer-visibility-${this.sectionId}`).forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const layerId = btn.dataset.layerId;
                    this.toggleLayerVisibility(layerId);
                });
            });
            
            // Lock toggle
            document.querySelectorAll(`.cts-layer-lock-${this.sectionId}`).forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const layerId = btn.dataset.layerId;
                    this.toggleLayerLock(layerId);
                });
            });
            
            // Drag and drop reordering
            const list = document.querySelector(`.cts-layers-list-${this.sectionId}`);
            if (list) {
                list.addEventListener('dragover', (e) => {
                    e.preventDefault();
                    const afterElement = this.getDragAfterElement(list, e.clientY);
                    const dragging = list.querySelector('.dragging');
                    if (dragging && afterElement == null) {
                        list.appendChild(dragging);
                    } else if (dragging && afterElement) {
                        list.insertBefore(dragging, afterElement);
                    }
                });
                
                list.addEventListener('drop', (e) => {
                    e.preventDefault();
                    const layerId = e.dataTransfer.getData('text/plain');
                    const dragging = list.querySelector('.dragging');
                    if (!dragging || !layerId) return;
                    
                    const items = Array.from(list.querySelectorAll(`.cts-layer-item-${this.sectionId}`));
                    const newIndex = items.indexOf(dragging);
                    
                    // Convert UI index (reverse order) to actual layer index
                    const actualIndex = this.layers.length - 1 - newIndex;
                    this.reorderLayer(layerId, actualIndex);
                });
            }
        }

        /**
         * Get element after which to insert dragged element
         */
        getDragAfterElement(container, y) {
            const draggableElements = [...container.querySelectorAll(`.cts-layer-item-${this.sectionId}:not(.dragging)`)];
            
            return draggableElements.reduce((closest, child) => {
                const box = child.getBoundingClientRect();
                const offset = y - box.top - box.height / 2;
                
                if (offset < 0 && offset > closest.offset) {
                    return { offset: offset, element: child };
                } else {
                    return closest;
                }
            }, { offset: Number.NEGATIVE_INFINITY }).element;
        }

        /**
         * Update properties panel UI
         */
        updatePropertiesPanel() {
            const panel = document.getElementById(`propertiesPanel-${this.sectionId}`);
            if (!panel) return;
            
            const layer = this.getSelectedLayer();
            if (!layer) {
                panel.innerHTML = '<div class="cts-no-selection-${this.sectionId}">No layer selected</div>';
                return;
            }
            
            // Build properties UI based on layer type
            let html = `
                <div class="cts-properties-header-${this.sectionId}">
                    <h3>Layer Properties</h3>
                    <div class="cts-properties-name-${this.sectionId}">${this.escapeHtml(layer.name)}</div>
                </div>
                
                <div class="cts-properties-section-${this.sectionId}">
                    <label>Opacity</label>
                    <div class="cts-property-control-${this.sectionId}">
                        <input type="range" 
                               class="cts-opacity-slider-${this.sectionId}" 
                               min="0" 
                               max="100" 
                               value="${layer.opacity}"
                               data-property="opacity">
                        <span class="cts-opacity-value-${this.sectionId}">${layer.opacity}%</span>
                    </div>
                </div>
                
                <div class="cts-properties-section-${this.sectionId}">
                    <label>Blend Mode</label>
                    <select class="cts-blend-mode-select-${this.sectionId}" data-property="blendMode">
                        <option value="normal" ${layer.blendMode === 'normal' ? 'selected' : ''}>Normal</option>
                        <option value="multiply" ${layer.blendMode === 'multiply' ? 'selected' : ''}>Multiply</option>
                        <option value="screen" ${layer.blendMode === 'screen' ? 'selected' : ''}>Screen</option>
                        <option value="overlay" ${layer.blendMode === 'overlay' ? 'selected' : ''}>Overlay</option>
                        <option value="soft-light" ${layer.blendMode === 'soft-light' ? 'selected' : ''}>Soft Light</option>
                        <option value="hard-light" ${layer.blendMode === 'hard-light' ? 'selected' : ''}>Hard Light</option>
                        <option value="color-dodge" ${layer.blendMode === 'color-dodge' ? 'selected' : ''}>Color Dodge</option>
                        <option value="color-burn" ${layer.blendMode === 'color-burn' ? 'selected' : ''}>Color Burn</option>
                        <option value="darken" ${layer.blendMode === 'darken' ? 'selected' : ''}>Darken</option>
                        <option value="lighten" ${layer.blendMode === 'lighten' ? 'selected' : ''}>Lighten</option>
                        <option value="difference" ${layer.blendMode === 'difference' ? 'selected' : ''}>Difference</option>
                        <option value="exclusion" ${layer.blendMode === 'exclusion' ? 'selected' : ''}>Exclusion</option>
                    </select>
                </div>
            `;
            
            // Add filters for images
            if (layer.type === 'image') {
                html += `
                    <div class="cts-properties-section-${this.sectionId}">
                        <label>Filters</label>
                        <div class="cts-filter-control-${this.sectionId}">
                            <label>Brightness</label>
                            <input type="range" 
                                   class="cts-filter-slider-${this.sectionId}" 
                                   min="-100" 
                                   max="100" 
                                   value="${layer.filters.brightness}"
                                   data-filter="brightness">
                            <span class="cts-filter-value-${this.sectionId}">${layer.filters.brightness}</span>
                        </div>
                        <div class="cts-filter-control-${this.sectionId}">
                            <label>Contrast</label>
                            <input type="range" 
                                   class="cts-filter-slider-${this.sectionId}" 
                                   min="-100" 
                                   max="100" 
                                   value="${layer.filters.contrast}"
                                   data-filter="contrast">
                            <span class="cts-filter-value-${this.sectionId}">${layer.filters.contrast}</span>
                        </div>
                        <div class="cts-filter-control-${this.sectionId}">
                            <label>Saturation</label>
                            <input type="range" 
                                   class="cts-filter-slider-${this.sectionId}" 
                                   min="-100" 
                                   max="100" 
                                   value="${layer.filters.saturation}"
                                   data-filter="saturation">
                            <span class="cts-filter-value-${this.sectionId}">${layer.filters.saturation}</span>
                        </div>
                    </div>
                `;
            }
            
            panel.innerHTML = html;
            
            // Setup property change listeners
            panel.querySelectorAll(`.cts-opacity-slider-${this.sectionId}`).forEach(slider => {
                slider.addEventListener('input', (e) => {
                    const value = parseInt(e.target.value);
                    const layer = this.getSelectedLayer();
                    if (layer) {
                        this.updateLayerProperty(layer.id, 'opacity', value);
                        e.target.nextElementSibling.textContent = value + '%';
                    }
                });
            });
            
            panel.querySelectorAll(`.cts-blend-mode-select-${this.sectionId}`).forEach(select => {
                select.addEventListener('change', (e) => {
                    const layer = this.getSelectedLayer();
                    if (layer) {
                        this.updateLayerProperty(layer.id, 'blendMode', e.target.value);
                    }
                });
            });
            
            panel.querySelectorAll(`.cts-filter-slider-${this.sectionId}`).forEach(slider => {
                slider.addEventListener('input', (e) => {
                    const filter = e.target.dataset.filter;
                    const value = parseInt(e.target.value);
                    const layer = this.getSelectedLayer();
                    if (layer) {
                        this.updateLayerProperty(layer.id, `filters.${filter}`, value);
                        e.target.nextElementSibling.textContent = value;
                    }
                });
            });
        }

        /**
         * Save state for undo/redo
         */
        saveState() {
            const state = JSON.parse(JSON.stringify(this.layers));
            this.undoStack.push(state);
            if (this.undoStack.length > this.maxUndo) {
                this.undoStack.shift();
            }
            this.redoStack = []; // Clear redo stack on new action
        }

        /**
         * Undo last action
         */
        undo() {
            if (this.undoStack.length === 0) return false;
            
            const currentState = JSON.parse(JSON.stringify(this.layers));
            this.redoStack.push(currentState);
            
            const previousState = this.undoStack.pop();
            this.layers = previousState;
            
            this.renderLayers();
            this.updateLayersPanel();
            this.updatePropertiesPanel();
            
            return true;
        }

        /**
         * Redo last undone action
         */
        redo() {
            if (this.redoStack.length === 0) return false;
            
            const currentState = JSON.parse(JSON.stringify(this.layers));
            this.undoStack.push(currentState);
            
            const nextState = this.redoStack.pop();
            this.layers = nextState;
            
            this.renderLayers();
            this.updateLayersPanel();
            this.updatePropertiesPanel();
            
            return true;
        }

        /**
         * Setup keyboard shortcuts
         */
        setupKeyboardShortcuts() {
            document.addEventListener('keydown', (e) => {
                // Only handle if studio is visible and not typing in input
                const studio = document.getElementById(`custom-tshirt-studio-${this.sectionId}`);
                if (!studio || studio.style.display === 'none') return;
                
                const activeElement = document.activeElement;
                if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
                    // Don't intercept if typing in input/textarea (unless it's Ctrl+Z/Y)
                    if (!(e.ctrlKey && (e.key === 'z' || e.key === 'y'))) {
                        return;
                    }
                }
                
                // Undo: Ctrl+Z or Ctrl+Shift+Z
                if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
                    e.preventDefault();
                    this.undo();
                    return;
                }
                
                // Redo: Ctrl+Y or Ctrl+Shift+Z
                if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'z')) {
                    e.preventDefault();
                    this.redo();
                    return;
                }
            });
        }

        /**
         * Escape HTML to prevent XSS
         */
        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }

        /**
         * Export layers data (for saving/loading)
         */
        exportLayers() {
            return JSON.parse(JSON.stringify(this.layers));
        }

        /**
         * Import layers data
         */
        importLayers(layersData) {
            this.layers = JSON.parse(JSON.stringify(layersData));
            this.nextLayerId = Math.max(...this.layers.map(l => parseInt(l.id.split('-')[1]) || 0)) + 1;
            this.saveState();
            this.renderLayers();
            this.updateLayersPanel();
            this.updatePropertiesPanel();
        }
    }

    // Export for use in other scripts
    window.LayerManager = LayerManager;
})();

